import {
  useSensor,
  DndContext,
  useSensors,
  DragOverlay,
  PointerSensor,
  closestCenter,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { Spin } from "antd";
import Group from "./Group";
import React, { useState } from "react";

import type {
  DragEndEvent,
  DragMoveEvent,
  DragStartEvent,
  UniqueIdentifier,
} from "@dnd-kit/core";
import type { TypeTagFilterItem } from ".";

export interface TypeDragLayoutProps<T = TypeTagFilterItem[]> {
  value?: T;
  onChange?(value: T, location: Record<"prv" | "next", number>): void;
  children?: React.ReactNode;
  loading?: boolean;
}

/**
 * @name DragLayout 只负责拖拽交互，拖拽后的数据结构变化，其他的不管
 */
const DragLayout: React.FC<TypeDragLayoutProps> = ({
  loading,
  onChange,
  children,
  value = [],
}) => {
  const sensors = useSensors(useSensor(PointerSensor));
  const [target, setTarget] = useState<TypeTagFilterItem>();
  const [drag, setDrag] = useState<{
    activeId?: UniqueIdentifier;
    overId?: UniqueIdentifier;
    direction?: "up" | "down";
  }>({});

  function onStart(e: DragStartEvent) {
    const i = value.findIndex((v) => v.id === e.active.id);
    setTarget(value[i]);
    setDrag({ activeId: e.active.id });
  }

  function onEnd({ active, over }: DragEndEvent) {
    if (over && active.id !== over.id) {
      const oldIndex = value.findIndex((v) => v.id === active.id);
      const newIndex = value.findIndex((v) => v.id === over.id);
      onChange?.(arrayMove(value, oldIndex, newIndex), {
        prv: oldIndex,
        next: newIndex,
      });
    }
    setTarget(undefined);
    setDrag({});
  }

  function onCancel() {
    setTarget(undefined);
    setDrag({});
  }

  function onDragMove(e: DragMoveEvent) {
    if (!e.active || !e.over) return setDrag({});
    const activeIndex = value.findIndex((v) => v.id === e.active.id);
    const overIndex = value.findIndex((v) => v.id === e.over.id);
    setDrag({
      overId: e.over.id,
      activeId: e.active.id,
      direction: activeIndex < overIndex ? "down" : "up",
    });
  }

  return (
    <Spin spinning={loading}>
      <DndContext
        sensors={sensors}
        onDragEnd={onEnd}
        onDragStart={onStart}
        onDragCancel={onCancel}
        onDragMove={onDragMove}
        collisionDetection={closestCenter}
      >
        <SortableContext items={value} strategy={verticalListSortingStrategy}>
          {React.Children.map(children, (child, idx) => {
            if (!React.isValidElement(child)) return child;
            const id = value[idx]?.id;
            let showLine = false;
            let linePos = "top";
            if (drag.activeId && drag.overId === id && drag.direction) {
              showLine = true;
              linePos = drag.direction === "down" ? "bottom" : "top";
            }
            return (
              <div key={id} className="sortable-place">
                {showLine && (
                  <div
                    style={{ [linePos]: -2 }}
                    className="sortable-place-line"
                  />
                )}
                {child}
              </div>
            );
          })}
        </SortableContext>
        {target?.id ? (
          <DragOverlay>
            <Group data={target} grabbing />
          </DragOverlay>
        ) : null}
      </DndContext>
    </Spin>
  );
};

export default DragLayout;
